// src/theme/index.js
import { createTheme } from '@mui/material/styles';

// Función para convertir hex a rgb
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? 
    `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` 
    : '0, 0, 0';
}

// Tema Dark (tu tema actual)
const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#2a4b8d',
      light: '#3a5b9d',
      dark: '#1a3b7d',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff9a3d',
      light: '#ffae5d',
      dark: '#e68a2d',
    },
    success: {
      main: '#4caf50',
      light: '#6fbf73',
      dark: '#3d8b40',
    },
    warning: {
      main: '#ff9800',
      light: '#ffac33',
      dark: '#e68900',
    },
    error: {
      main: '#f44336',
      light: '#f6685e',
      dark: '#d32f2f',
    },
    background: {
      default: '#0a0f1a',
      paper: '#1a1f2e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  }
};

// Tema Light (nuevo - opuesto al dark)
const lightTheme = {
  palette: {
    mode: 'light',
    primary: {
      main: '#2a4b8d',      // Mismo azul principal
      light: '#3a5b9d',
      dark: '#1a3b7d',
      contrastText: '#ffffff', // Texto blanco sobre azul
    },
    secondary: {
      main: '#ff9a3d',
      light: '#ffae5d',
      dark: '#e68a2d',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    background: {
      default: '#f8f9fa',   // Fondo claro
      paper: '#ffffff',     // Cards blancos
    },
    text: {
      primary: '#1a1a1a',   // Texto oscuro
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  }
};

// Configuración común para ambos temas
const commonThemeConfig = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '0.9375rem',
        },
        containedPrimary: {
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          boxShadow: (theme) => `0 4px 14px rgba(${hexToRgb(theme.palette.primary.main)}, 0.4)`,
          color: (theme) => theme.palette.primary.contrastText,
          '&:hover': {
            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            boxShadow: (theme) => `0 6px 20px rgba(${hexToRgb(theme.palette.primary.main)}, 0.5)`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: (theme) => 
            theme.palette.mode === 'dark' 
              ? `linear-gradient(145deg, rgba(26, 31, 46, 0.9) 0%, rgba(42, 75, 141, 0.3) 100%)`
              : `linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(42, 75, 141, 0.1) 100%)`,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': {
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: (theme) => theme.palette.mode === 'dark' ? '#0f141f' : '#ffffff',
          borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(10, 15, 26, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      },
    },
  },
};

// Función para crear el tema.
// Fase 5 Redeprog SaaS: acepta overrides opcionales de branding (paleta y
// tipografía). En modo Fenagro se llama sin overrides (comportamiento
// clásico); en modo Redeprog se pasan los datos de la Organizacion activa
// para que cada tenant se vea con su identidad.
export const createAppTheme = (isDarkMode = true, brandingOverrides = null) => {
  const baseTheme = isDarkMode ? darkTheme : lightTheme;

  const paletteOverride = brandingOverrides ? {
    palette: {
      ...baseTheme.palette,
      ...(brandingOverrides.colorPrimario && {
        primary: { ...baseTheme.palette.primary, main: brandingOverrides.colorPrimario },
      }),
      ...(brandingOverrides.colorSecundario && {
        secondary: { ...baseTheme.palette.secondary, main: brandingOverrides.colorSecundario },
      }),
    },
  } : {};

  const typographyOverride = brandingOverrides?.tipografia ? {
    typography: {
      ...(commonThemeConfig.typography || {}),
      fontFamily: brandingOverrides.tipografia,
    },
  } : {};

  return createTheme({
    ...baseTheme,
    ...commonThemeConfig,
    ...paletteOverride,
    ...typographyOverride,
  });
};

// Tema por defecto (para compatibilidad)
const defaultTheme = createAppTheme(true);
export default defaultTheme;